/**
 * Conversion tracking (Facebook Ads and TikTok Ads).
 * fbclid/ttclid are captured from the URL and persisted in localStorage so they
 * survive navigation and are available at signup and on the signup success page.
 */

const STORAGE_KEYS = { fbclid: 'curipod_fbclid', ttclid: 'curipod_ttclid' } as const;

type TrackingParam = keyof typeof STORAGE_KEYS;

const TRACKING_PARAMS = Object.keys(STORAGE_KEYS) as TrackingParam[];

function getParamFromUrl(param: TrackingParam, url?: string): string | null {
  try {
    const href = url || (typeof window !== 'undefined' ? window.location.href : '');
    return new URL(href).searchParams.get(param);
  } catch {
    return null;
  }
}

function storeParam(param: TrackingParam, value: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    localStorage.setItem(STORAGE_KEYS[param], value);
    return true;
  } catch {
    return false;
  }
}

function getStoredParam(param: TrackingParam): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(STORAGE_KEYS[param]);
  } catch {
    return null;
  }
}

function getTrackingParam(param: TrackingParam): string | null {
  const fromUrl = getParamFromUrl(param);
  if (fromUrl) {
    storeParam(param, fromUrl);
    return fromUrl;
  }
  return getStoredParam(param);
}

/** Call on app mount to capture fbclid/ttclid from URL into localStorage. */
export function captureTrackingParamsFromUrl() {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  const detected: Record<string, string> = {};
  let storeSuccessful = true;

  for (const param of TRACKING_PARAMS) {
    const value = urlParams.get(param);
    if (!value) continue;
    detected[param] = value;
    if (!storeParam(param, value)) storeSuccessful = false;
  }

  if (Object.keys(detected).length === 0) return null;
  return { detected, storeSuccessful };
}

/** fbclid from URL or localStorage (URL preferred). */
export function getFbclid(): string | null {
  return getTrackingParam('fbclid');
}

/** ttclid from URL or localStorage (URL preferred). */
export function getTtclid(): string | null {
  return getTrackingParam('ttclid');
}

/** Both tracking params; used when building signup callback URL or conversion payload. */
export function getTrackingParams(): { fbclid?: string; ttclid?: string } {
  const fbclid = getFbclid();
  const ttclid = getTtclid();
  return { ...(fbclid && { fbclid }), ...(ttclid && { ttclid }) };
}

/**
 * Wrap a post-auth destination so signup routes through /signup/success, which
 * fires the ad-conversion (CompleteRegistration) + analytics before redirecting
 * to the destination. Used by both the legacy B2C signup hook and the new
 * passwordless/OAuth signup flow.
 */
export function buildSignupSuccessCallback(destination: string): string {
  const { fbclid, ttclid } = getTrackingParams();
  let url = `/signup/success?callbackURI=${encodeURIComponent(destination)}`;
  if (fbclid) url += `&fbclid=${fbclid}`;
  if (ttclid) url += `&ttclid=${ttclid}`;
  return url;
}

/** Format for Meta Conversion API: fb.{subdomainIndex}.{creationTime}.{fbclid} */
export function convertFbclidToFbc(fbclid: string): string {
  if (!fbclid) throw new Error('fbclid is required');
  return `fb.1.${Date.now()}.${fbclid}`;
}

export function getUserAgent(): string {
  return typeof window === 'undefined' ? '' : window.navigator.userAgent;
}
